import { Conversion, t } from "@macrograph/module";
import { Result } from "effect";

import type { Connection } from "./Connection.ts";

import * as OutputRef from "./OutputRef.ts";

interface DataPort {
  readonly id: string;
  readonly type: t.Type;
}
interface ExecutionPort {
  readonly id: string;
  readonly scope?: ReadonlyArray<DataPort> | null | undefined;
}
export interface IO {
  readonly dataInputs: ReadonlyArray<DataPort>;
  readonly dataOutputs: ReadonlyArray<DataPort>;
  readonly executionInputs: ReadonlyArray<ExecutionPort>;
  readonly executionOutputs: ReadonlyArray<ExecutionPort>;
}

const declarationKey = (io: IO): string => {
  const data = ({ id, type }: DataPort) => ({ id, type });
  const execution = ({ id, scope }: ExecutionPort) => ({
    id,
    scope: scope == null ? scope : scope.map(data),
  });
  // Names, defaults and runtime suggestion callbacks do not constrain type variables.
  return JSON.stringify({
    dataInputs: io.dataInputs.map(data),
    dataOutputs: io.dataOutputs.map(data),
    executionInputs: io.executionInputs.map(execution),
    executionOutputs: io.executionOutputs.map(execution),
  });
};

/** Preserve port metadata while substituting types, without mutating declarations. */
export const mapIO = <T extends IO>(io: T, resolve: (type: t.Type) => t.Type): T => {
  const data = <P extends DataPort>(port: P): P => ({ ...port, type: resolve(port.type) });
  const execution = <P extends ExecutionPort>(port: P): P => ({
    ...port,
    ...(port.scope == null ? {} : { scope: port.scope.map(data) }),
  });
  return {
    ...io,
    dataInputs: io.dataInputs.map(data),
    dataOutputs: io.dataOutputs.map(data),
    executionInputs: io.executionInputs.map(execution),
    executionOutputs: io.executionOutputs.map(execution),
  };
};

interface Term {
  readonly node: string;
  readonly type: t.Type;
}
interface Pending {
  readonly wire: Connection.Model;
  readonly source: Term;
  readonly target: Term;
}
const key = (term: Term & { type: t.Wildcard }) => JSON.stringify([term.node, term.type.id]);
const child = (term: Term): Term | undefined =>
  term.type._tag === "List"
    ? { ...term, type: term.type.item }
    : term.type._tag === "Option"
      ? { ...term, type: term.type.inner }
      : undefined;

export interface Conflict {
  readonly nodes: ReadonlySet<string>;
  readonly connectionId: string;
  readonly reason: string;
}
export interface Group {
  readonly nodes: ReadonlySet<string>;
  readonly connections: ReadonlyArray<Connection.Model>;
  readonly resolve: (node: string, type: t.Type) => t.Type;
}

const solve = (
  nodes: ReadonlySet<string>,
  connections: ReadonlyArray<Connection.Model>,
  io: ReadonlyMap<string, IO>,
  conversions: Conversion.Rules,
): Result.Result<Group, ReadonlyArray<Conflict>> => {
  const bindings = new Map<string, Term>();
  const bound: string[] = [];
  const conflicts: Conflict[] = [];
  const constraint = (wildcard: t.Wildcard) =>
    wildcard.id === "Struct" || wildcard.id === "Enum" || wildcard.id === "Type"
      ? wildcard.id
      : undefined;
  const accepts = (wildcard: t.Wildcard, type: t.Type) => {
    const expected = constraint(wildcard);
    if (expected === undefined || type._tag === "Wildcard") return true;
    if (type._tag !== "Struct" && type._tag !== "Enum") return false;
    return expected === "Type" || type._tag === expected;
  };
  const dereference = (term: Term): Term => {
    while (term.type._tag === "Wildcard") {
      const next = bindings.get(key({ ...term, type: term.type }));
      if (next === undefined) break;
      term = next;
    }
    return term;
  };
  const occurs = (id: string, term: Term): boolean => {
    term = dereference(term);
    if (term.type._tag === "Wildcard") return key({ ...term, type: term.type }) === id;
    const inner = child(term);
    return inner !== undefined && occurs(id, inner);
  };
  const unify = (a: Term, b: Term): boolean => {
    a = dereference(a);
    b = dereference(b);
    if (a.type._tag === "Wildcard") {
      const id = key({ ...a, type: a.type });
      if (b.type._tag === "Wildcard") {
        if (key({ ...b, type: b.type }) === id) return true;
        const aConstraint = constraint(a.type);
        const bConstraint = constraint(b.type);
        if (aConstraint !== undefined && bConstraint !== undefined && aConstraint !== bConstraint) {
          // `Type` accepts any Struct or Enum, so it narrows to the other constraint.
          if (bConstraint === "Type") return unify(b, a);
          if (aConstraint !== "Type") return false;
        }
        if (aConstraint !== undefined && bConstraint === undefined) return unify(b, a);
      }
      if (!accepts(a.type, b.type)) return false;
      if (occurs(id, b)) return false;
      bindings.set(id, b);
      bound.push(id);
      return true;
    }
    if (b.type._tag === "Wildcard") return unify(b, a);
    if (a.type._tag !== b.type._tag) return false;
    const ac = child(a),
      bc = child(b);
    return ac !== undefined && bc !== undefined ? unify(ac, bc) : t.equals(a.type, b.type);
  };
  // Current value of a term, without caching: bindings may still change during solving.
  const current = (term: Term): t.Type => {
    const next = dereference(term);
    if (next.type._tag === "List") return t.List(current({ ...next, type: next.type.item }));
    if (next.type._tag === "Option") return t.Option(current({ ...next, type: next.type.inner }));
    return next.type;
  };
  const attempt = (pending: Pending, check: () => boolean) => {
    const bindingCount = bound.length;
    if (check()) return;
    while (bound.length > bindingCount) bindings.delete(bound.pop()!);
    conflicts.push({
      nodes,
      connectionId: pending.wire.id,
      reason: "Conflicting or recursive wildcard types",
    });
  };
  // Input-side wires and scope fields must agree exactly. Outputs whose wildcard is still
  // unresolved after that are bound by their targets and must agree with each other. Outputs
  // already resolved may use a registered conversion, so the result is independent of wire order.
  const equal: Pending[] = [];
  const outgoing: Pending[] = [];
  for (const wire of connections) {
    if (!nodes.has(wire.outNodeId)) continue;
    const source = io.get(wire.outNodeId),
      target = io.get(wire.inNodeId);
    if (source === undefined || target === undefined) continue;
    const output = OutputRef.resolve(source, wire.outIo);
    const inputs = target.dataInputs.filter((port) => port.id === wire.inIoId);
    const pair = (a: DataPort, b: DataPort, convertible: boolean) => {
      // Ordinary concrete incompatibilities are handled by endpoint validation.
      if (!t.hasWildcard(a.type) && !t.hasWildcard(b.type)) return;
      const pending: Pending = {
        wire,
        source: { node: wire.outNodeId, type: a.type },
        target: { node: wire.inNodeId, type: b.type },
      };
      const isOutput = convertible && t.hasWildcard(a.type) && !t.hasWildcard(b.type);
      (isOutput ? outgoing : equal).push(pending);
    };
    if (output?.kind === "data" && inputs.length === 1) pair(output.port, inputs[0]!, true);
    if (output?.kind === "execution" && output.port.scope != null) {
      const input = target.executionInputs.find((port) => port.id === wire.inIoId);
      // An inferred scope input exposes its fields as data outputs on a projection.
      const fields = input?.scope === null ? target.dataOutputs : input?.scope;
      for (const field of fields ?? []) {
        const source = output.port.scope.find((candidate) => candidate.id === field.id);
        if (source !== undefined) pair(source, field, false);
      }
    }
  }
  for (const pending of equal) attempt(pending, () => unify(pending.source, pending.target));
  const unresolved = new Set(outgoing.filter((pending) => t.hasWildcard(current(pending.source))));
  for (const pending of unresolved) attempt(pending, () => unify(pending.source, pending.target));
  for (const pending of outgoing)
    if (!unresolved.has(pending)) {
      if (!conversions.has(current(pending.source), pending.target.type))
        conflicts.push({
          nodes,
          connectionId: pending.wire.id,
          reason: "Resolved wildcard type has no registered conversion to the connected input",
        });
    }
  if (conflicts.length > 0) return Result.fail(conflicts);
  const values = new Map<string, t.Type>();
  const resolve = (node: string, type: t.Type): t.Type => {
    const id = type._tag === "Wildcard" ? key({ node, type }) : undefined;
    const cached = id === undefined ? undefined : values.get(id);
    if (cached !== undefined) return cached;
    const term = dereference({ node, type });
    const value =
      term.type._tag === "List"
        ? t.List(resolve(term.node, term.type.item))
        : term.type._tag === "Option"
          ? t.Option(resolve(term.node, term.type.inner))
          : term.type;
    if (id !== undefined) values.set(id, value);
    return value;
  };
  return Result.succeed({ nodes, connections, resolve });
};

/**
 * Explicit, non-reactive connected-component cache. Nodes conservatively group all their
 * pins; each node-local wildcard still has a separate unification variable. Updates diff
 * declarations/wires, invalidate their OLD groups, then crawl the NEW adjacency to handle
 * merges, splits, removed anchors, cycles, and deleted nodes. Unaffected groups are reused.
 */
export class Cache {
  private declarations = new Map<string, string>();
  private wires = new Map<string, Connection.Model>();
  // Include non-data wires too: an IO change can turn an exec port into a data port.
  private incident = new Map<string, ReadonlyMap<string, Connection.Model>>();
  private byNode = new Map<string, Group>();

  /** Rules are fixed per cache: different rules (e.g. after a module mounts) need a new cache. */
  constructor(private readonly conversions: Conversion.Rules = Conversion.defaultRules) {}

  get groups(): ReadonlySet<Group> {
    return new Set(this.byNode.values());
  }
  group(node: string): Group | undefined {
    return this.byNode.get(node);
  }
  resolve(node: string, type: t.Type): t.Type {
    return this.byNode.get(node)?.resolve(node, type) ?? type;
  }
  resolveIO<T extends IO>(node: string, io: T): T {
    return mapIO(io, (type) => this.resolve(node, type));
  }

  /** Validate a candidate graph, committing all indexes/groups only if it is valid. */
  update(
    io: ReadonlyMap<string, IO>,
    connections: ReadonlyArray<Connection.Model>,
  ): Result.Result<void, ReadonlyArray<Conflict>> {
    return this.updateOnce(io, connections);
  }

  private updateOnce(
    io: ReadonlyMap<string, IO>,
    connections: ReadonlyArray<Connection.Model>,
  ): Result.Result<void, ReadonlyArray<Conflict>> {
    const dirty = new Set<string>();
    const declarations = new Map(Array.from(io, ([id, ports]) => [id, declarationKey(ports)]));
    for (const id of new Set([...this.declarations.keys(), ...declarations.keys()]))
      if (this.declarations.get(id) !== declarations.get(id)) dirty.add(id);
    const wires = new Map<string, Connection.Model>(connections.map((wire) => [wire.id, wire]));
    // Copy-on-write buckets: a rejected attempt cannot change the live adjacency index.
    const changedIncident = new Map<string, Map<string, Connection.Model>>();
    const editIncident = (node: string) => {
      let bucket = changedIncident.get(node);
      if (bucket === undefined) {
        bucket = new Map(this.incident.get(node));
        changedIncident.set(node, bucket);
      }
      return bucket;
    };
    for (const id of new Set([...this.wires.keys(), ...wires.keys()])) {
      const before = this.wires.get(id),
        after = wires.get(id);
      if (JSON.stringify(before) === JSON.stringify(after)) continue;
      if (before !== undefined) {
        editIncident(before.outNodeId).delete(id);
        editIncident(before.inNodeId).delete(id);
      }
      if (after !== undefined) {
        editIncident(after.outNodeId).set(id, after);
        editIncident(after.inNodeId).set(id, after);
      }
      for (const wire of [before, after])
        if (wire !== undefined) {
          dirty.add(wire.outNodeId);
          dirty.add(wire.inNodeId);
        }
    }
    if (dirty.size === 0) return Result.succeed(undefined);
    const affected = new Set<Group>();
    for (const id of dirty) {
      const group = this.byNode.get(id);
      if (group !== undefined) affected.add(group);
    }
    for (const group of affected) for (const member of group.nodes) dirty.add(member);
    const completed: Group[] = [];
    const conflicts: Conflict[] = [];
    const visited = new Set<string>();
    const order = new Map(connections.map((wire, index) => [wire.id, index]));
    for (const id of dirty) {
      if (!io.has(id) || visited.has(id)) continue;
      const members = new Set<string>(),
        pending = [id];
      const direct = new Map<string, Connection.Model>();
      while (pending.length > 0) {
        const node = pending.pop()!;
        if (members.has(node)) continue;
        members.add(node);
        visited.add(node);
        for (const wire of (changedIncident.get(node) ?? this.incident.get(node))?.values() ?? []) {
          if (!io.has(wire.outNodeId) || !io.has(wire.inNodeId)) continue;
          const output = OutputRef.resolve(io.get(wire.outNodeId)!, wire.outIo);
          // Plain control flow does not couple type variables.
          if (output === undefined || (output.kind === "execution" && output.port.scope == null))
            continue;
          direct.set(wire.id, wire);
          pending.push(wire.outNodeId === node ? wire.inNodeId : wire.outNodeId);
        }
      }
      // Preserve wire order so invalid saved graphs report deterministic conflicts.
      const groupWires = [...direct.values()].sort((a, b) => order.get(a.id)! - order.get(b.id)!);
      const group = solve(members, groupWires, io, this.conversions);
      if (Result.isFailure(group)) conflicts.push(...group.failure);
      else completed.push(group.success);
    }
    if (conflicts.length > 0) return Result.fail(conflicts);
    for (const id of dirty) this.byNode.delete(id);
    for (const group of completed) for (const node of group.nodes) this.byNode.set(node, group);
    for (const [node, bucket] of changedIncident) {
      if (bucket.size === 0) this.incident.delete(node);
      else this.incident.set(node, bucket);
    }
    this.declarations = declarations;
    this.wires = wires;
    return Result.succeed(undefined);
  }
}

export * as Wildcards from "./Wildcards.ts";
